import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';
import { createPortal } from 'react-dom';

import { copyDocumentStyles } from './copy-document-styles';
import {
    DocumentPictureInPictureContext,
    type DocumentPictureInPictureContextValue,
} from './document-picture-in-picture-context';
import css from './styles.css';

const MEDIA_SESSION_ENTER_PIP = 'enterpictureinpicture' as const;

function setupPipWindow(pipWin: Window, copyStyles: boolean, onPageHide: () => void): HTMLDivElement {
    pipWin.addEventListener('pagehide', onPageHide, { once: true });

    const pipDoc = pipWin.document;
    pipDoc.documentElement.style.height = '100%';
    pipDoc.body.style.margin = '0';
    pipDoc.body.style.minHeight = '100%';
    pipDoc.body.style.height = '100%';

    if (copyStyles) {
        copyDocumentStyles(document, pipDoc);
    }

    const root = pipDoc.createElement('div');
    root.className = css.root;
    pipDoc.body.appendChild(root);
    return root;
}

type MediaSessionWithEnterPip = {
    setActionHandler(action: typeof MEDIA_SESSION_ENTER_PIP, handler: (() => void | Promise<void>) | null): void;
};

export interface AutoEnterDocumentPiPProps {
    /** При изменении значения окно Document PiP закрывается */
    closeRequestId: number;
    onOpenChange?: (open: boolean) => void;
    width?: number;
    height?: number;
    copyOpenerStyles?: boolean;
    className?: string;
    children: React.ReactNode;
}

export const AutoEnterDocumentPiP: React.FC<AutoEnterDocumentPiPProps> = ({
    closeRequestId,
    onOpenChange,
    width,
    height,
    copyOpenerStyles = true,
    className,
    children,
}) => {
    const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
    const pipWindowRef = useRef<Window | null>(null);
    // Инкремент при закрытии PiP/unmount, чтобы отменить колбэк после пендинг requestWindow()
    const pipRequestEpochRef = useRef(0);
    const prevCloseRequestIdRef = useRef<number | undefined>(undefined);
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;

    const widthRef = useRef(width);
    const heightRef = useRef(height);
    const copyOpenerStylesRef = useRef(copyOpenerStyles);
    widthRef.current = width;
    heightRef.current = height;
    copyOpenerStylesRef.current = copyOpenerStyles;

    const closePipWindow = useCallback(() => {
        pipRequestEpochRef.current++;
        setPortalContainer(null);
        const w = pipWindowRef.current;
        if (w) {
            w.close();
        }
        pipWindowRef.current = null;
    }, []);

    useEffect(() => {
        if (prevCloseRequestIdRef.current !== undefined && prevCloseRequestIdRef.current !== closeRequestId) {
            closePipWindow();
            onOpenChangeRef.current?.(false);
        }
        prevCloseRequestIdRef.current = closeRequestId;
    }, [closeRequestId, closePipWindow]);

    /** Единственная точка открытия PiP: Chrome вызывает enterpictureinpicture (уход со вкладки и т.п.). */
    useEffect(() => {
        if (!window.documentPictureInPicture) {
            return;
        }

        const handler = async () => {
            if (pipWindowRef.current) {
                return;
            }

            const api = window.documentPictureInPicture;
            if (!api) {
                return;
            }
            const epochAtRequest = pipRequestEpochRef.current;
            try {
                const pipWin = await api.requestWindow({
                    width: widthRef.current,
                    height: heightRef.current,
                });

                if (pipRequestEpochRef.current !== epochAtRequest) {
                    pipWin.close();
                    return;
                }

                if (pipWindowRef.current) {
                    pipWin.close();
                    return;
                }

                const onPipPageHide = () => {
                    pipWindowRef.current = null;
                    setPortalContainer(null);
                    onOpenChangeRef.current?.(false);
                };

                pipWindowRef.current = pipWin;
                const root = setupPipWindow(pipWin, copyOpenerStylesRef.current, onPipPageHide);
                setPortalContainer(root);
                onOpenChangeRef.current?.(true);
            } catch {
                /* requestWindow failed */
            }
        };

        try {
            (navigator.mediaSession as unknown as MediaSessionWithEnterPip).setActionHandler(
                MEDIA_SESSION_ENTER_PIP,
                handler
            );
        } catch {
            /* setActionHandler(enterpictureinpicture) not supported */
        }

        return () => {
            try {
                (navigator.mediaSession as unknown as MediaSessionWithEnterPip).setActionHandler(
                    MEDIA_SESSION_ENTER_PIP,
                    null
                );
            } catch {
                /* ignore */
            }
        };
    }, []);

    useEffect(() => {
        return () => {
            closePipWindow();
        };
    }, [closePipWindow]);

    useLayoutEffect(() => {
        if (portalContainer) {
            portalContainer.className = cn(css.root, className);
        }
    }, [portalContainer, className]);

    const pipEnv = useMemo((): DocumentPictureInPictureContextValue | null => {
        if (!portalContainer) {
            return null;
        }
        const pipDocument = portalContainer.ownerDocument;
        const pipWindow = pipDocument.defaultView;
        if (!pipWindow) {
            return null;
        }
        return { pipDocument, pipWindow };
    }, [portalContainer]);

    if (!portalContainer || !pipEnv) {
        return null;
    }

    return createPortal(
        <DocumentPictureInPictureContext.Provider value={pipEnv}>{children}</DocumentPictureInPictureContext.Provider>,
        portalContainer
    );
};
